import { supabase } from '@/services/supabase'
import { resolveBootSave, type BootSaveResult } from '@/services/bootSave'

/**
 *  THE BOOT SEQUENCE, AND WHY IT BLOCKS.
 *
 *  Everything in here runs before `@/store/store` is imported. That ordering is
 *  the fix for a bug reported twice:
 *
 *    "my character is 95 and on tablet its 97 and wont update on the phone"
 *
 *  The store is built from localStorage at module-evaluation time, so a cloud
 *  save that arrived later could only be applied by writing localStorage and
 *  reloading the page. The reload was capped at one per tab session, an
 *  installed PWA never clears sessionStorage on resume, and the device that
 *  spent its reload was then told to "close and reopen" — which did nothing.
 *  Saving stayed paused and the phone sat on the old character.
 *
 *  Daniel, 2026-10-16: *"how ever you need to do it get it done whether loading
 *  times are longer and have a game loading screen"*. So: one round trip before
 *  the game mounts, and a loading screen that says what it is waiting for.
 *
 *  The common case — same device, nothing changed elsewhere — costs ONE cheap
 *  `save_rev()` call and no blob fetch at all, because the revision answers the
 *  question on its own.
 */

/** Stages the HTML loading screen renders. Ordered; each one replaces the last. */
export type BootStage =
  | 'start'
  | 'session'
  | 'save'
  | 'mount'
  | 'ready'

export interface BootProgress {
  stage: BootStage
  /** 0..1, for the bar. */
  progress: number
  /** Player-facing line. Short, and true. */
  label: string
}

const STAGES: Record<BootStage, { progress: number; label: string }> = {
  start:   { progress: 0.08, label: 'Waking the realm' },
  session: { progress: 0.28, label: 'Signing you in' },
  save:    { progress: 0.55, label: 'Finding your character' },
  mount:   { progress: 0.82, label: 'Building the world' },
  ready:   { progress: 1,    label: 'Ready' },
}

/**
 *  The loading screen lives in index.html so it paints before any JavaScript
 *  parses. It exposes this hook; we call it if present and ignore it if not,
 *  so boot never depends on the DOM shell being a particular shape.
 */
function report(stage: BootStage): void {
  const s = STAGES[stage]
  try {
    const w = window as unknown as { __prBoot?: (p: BootProgress) => void }
    w.__prBoot?.({ stage, progress: s.progress, label: s.label })
  } catch { /* the screen is decoration; never let it break boot */ }
}

export interface BootResult {
  userId: string | null
  save: BootSaveResult | null
}

/**
 *  How long we are willing to wait for the network before starting anyway.
 *
 *  A blocking boot must have a floor, or a dead connection becomes a dead
 *  game. On timeout we start on the local save with pushing blocked, which is
 *  exactly the 'unreachable' contract — the player can read their character
 *  and nothing can overwrite the cloud.
 */
const BOOT_NETWORK_BUDGET_MS = 12_000

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>(resolve => {
    let settled = false
    const t = window.setTimeout(() => {
      if (!settled) { settled = true; resolve(fallback) }
    }, ms)
    p.then(v => {
      if (!settled) { settled = true; window.clearTimeout(t); resolve(v) }
    }, () => {
      if (!settled) { settled = true; window.clearTimeout(t); resolve(fallback) }
    })
  })
}

/**
 *  Resolve auth and the authoritative save, then hand back to main to mount.
 *
 *  Never throws and never hangs: every branch resolves, and the slow ones are
 *  wrapped in a budget. A boot that cannot finish still has to start the game.
 */
export async function runBoot(): Promise<BootResult> {
  report('start')

  // Local-only mode (no backend configured) has nothing to resolve.
  if (!supabase) {
    report('mount')
    return { userId: null, save: null }
  }

  report('session')
  const session = await withTimeout(
    supabase.auth.getSession().then(r => r.data.session ?? null).catch(() => null),
    BOOT_NETWORK_BUDGET_MS,
    null,
  )
  const userId = session?.user?.id ?? null

  // Not signed in: the sign-in screen is next, and there is no account save to
  // resolve. A guest's local save is loaded by the store as it always was.
  if (!userId) {
    report('mount')
    return { userId: null, save: null }
  }

  report('save')
  const save = await withTimeout(
    resolveBootSave(userId),
    BOOT_NETWORK_BUDGET_MS,
    { status: 'unreachable' as const, rev: null, detail: 'Took too long to reach the server' },
  )
  console.info(`[boot] save: ${save.status} (rev ${save.rev ?? '?'}) — ${save.detail}`)

  report('mount')
  return { userId, save }
}

export function reportBootStage(stage: BootStage): void { report(stage) }
